import { useState } from 'react';

import { DeleteOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import {
	Button,
	Checkbox,
	Flex,
	Form,
	Input,
	Select,
	Tooltip,
	Typography,
} from 'antd';
import cn from 'classnames';

import styles from './styles.module.css';

type TProps = {
	keyProp: number;
	name: number;
	restField: {
		fieldKey?: number;
	};
	deleteDisabled: boolean;
	removePrompt?: (id: number | string) => void;
	remove: (index: number | number[]) => void;
	hasError?: boolean;
};

export default function PromptItem({
	deleteDisabled,
	removePrompt,
	keyProp,
	remove,
	name,
	restField,
	hasError,
}: TProps) {
	const [isCollapsed, setIsCollapsed] = useState(name > 0);
	const [evalSchemaValues, setEvalSchemaValues] = useState<string[]>([]);

	return (
		<div
			key={keyProp}
			style={{
				// width: "max-content",
				border: `1px solid ${hasError ? '#ff4d4f' : '#ccc'}`,
				padding: '16px',
				marginBottom: '16px',
				borderRadius: '8px',
				position: 'relative',
			}}
			//   className={styles.container}
		>
			<Flex
				justify="space-between"
				align="center"
				style={{ marginBottom: isCollapsed ? 0 : '0.5rem' }}
			>
				<Typography.Title level={5} style={{ margin: 0 }}>
					Prompt {name + 1}
				</Typography.Title>

				<Flex justify="space-between" align="center" gap={8}>
					<Tooltip title="Delete Prompt" placement="left">
						<Button
							size="small"
							type="dashed"
							danger
							onClick={() => remove(name)}
							disabled={deleteDisabled}
						>
							<DeleteOutlined />
						</Button>
					</Tooltip>

					<Tooltip title="Expand/collapse" placement="left">
						<Button
							size="small"
							type="default"
							onClick={() => setIsCollapsed((cur) => !cur)}
							// style={{
							//   position: "absolute",
							//   top: "8px",
							//   right: "8px",
							// }}
						>
							{isCollapsed ? <UpOutlined /> : <DownOutlined />}
						</Button>
					</Tooltip>
				</Flex>
			</Flex>

			<div
				className={cn(
					styles.content,
					isCollapsed ? styles.closed : styles.open,
				)}
			>
				<Form.Item
					{...restField}
					name={[name, 'title']}
					label="Title"
					shouldUpdate
					rules={[
						{ required: true, message: 'Title is required.' },
						{ whitespace: true, message: 'Title cannot be empty.' },
					]}
					style={{ margin: 0 }}
				>
					<Input placeholder="Title" />
				</Form.Item>
				<Form.Item
					{...restField}
					name={[name, 'prompt']}
					label="Prompt"
					shouldUpdate
					rules={[{ required: true, message: 'Prompt is required.' }]}
					style={{ margin: 0 }}
				>
					<Input.TextArea
						placeholder="Prompt"
						rows={2}
						style={{ resize: 'none' }}
					/>
				</Form.Item>
				<Form.Item
					shouldUpdate
					{...restField}
					name={[name, 'eval_schema']}
					style={{ margin: 0 }}
					label="Evaluation Schema"
					// initialValue={[]}
				>
					<Select
						mode="tags"
						placeholder="Evaluation Schema"
						onChange={(e) => setEvalSchemaValues(e as string[])}
					/>
				</Form.Item>
				<Form.Item
					key={evalSchemaValues?.toString()}
					shouldUpdate
					dependencies={[name, 'eval_schema']}
					{...restField}
					// hasFeedback
					rules={[
						{
							required: true,
							message: 'Trigger is required',
						},
						({ getFieldsValue }) => ({
							validator(_, value) {
								const eval_schema =
									getFieldsValue()?.prompts?.[name]?.eval_schema;

								const filtered = value?.filter(
									(item: string) =>
										!eval_schema?.includes(item) || item === 'any',
								);

								if (
									(value?.length === 1 && value?.[0] === 'any') ||
									(value?.length > 0 && filtered?.length === 0)
								) {
									return Promise.resolve();
								}

								const errMessage =
									eval_schema?.length > 0
										? `Trigger must be one/multiple from the evaluation schema (${eval_schema
												?.map((i: string) => `'${i}'`)
												?.join(',')}) or "any"`
										: `Trigger must be one/multiple from the evaluation schema or "any"`;

								return Promise.reject(new Error(errMessage));
							},
						}),
					]}
					name={[name, 'trigger']}
					style={{ margin: 0 }}
					label="Trigger"
					// initialValue={[]}
				>
					<Select
						mode="tags"
						placeholder="Trigger"
						options={
							evalSchemaValues?.length > 0
								? evalSchemaValues?.map((item: string) => ({
										label: item,
										value: item,
								  }))
								: [
										{
											label: 'any',
											value: 'any',
										},
								  ]
						}
					/>
					{/* <Input.TextArea
            placeholder="Trigger"
            rows={2}
            style={{ resize: "none" }}
          /> */}
				</Form.Item>
			</div>
		</div>
	);
}
